import { z } from "zod";
import { LISTING_STATUS } from "../enums.js";
import { CONTRACT_TYPES, PROPERTY_TYPES, PORTAL_KEYS } from "./portal.js";

// Field key whitelist — exactly the keys an AI suggestion or content_version payload may use.
export const ALLOWED_FIELD_KEYS = [
  "title", "description", "city", "zone", "province", "region", "country",
  "lat", "lng", "prezzo", "mq", "rooms", "bathrooms", "ape", "ipe", "piano",
  "disponibilita", "kind", "features", "anno_costruzione", "spese_condominio",
  "riscaldamento", "esposizione", "stato_conservazione",
] as const;
export type FieldKey = (typeof ALLOWED_FIELD_KEYS)[number];

export const CreateListingInput = z.object({
  slug: z.string().min(2).max(120).regex(/^[a-z0-9-]+$/, "slug must be kebab-case ASCII"),
  kind: z.enum(["immobile", "cantiere"]),
  title: z.record(z.string()).optional(),
  city: z.string().min(2).max(120).optional(),
  zone: z.string().max(120).optional(),
  province: z.string().max(4).optional(),
  region: z.string().max(60).optional(),
  country: z.string().length(2).default("IT"),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
});
export type CreateListingInput = z.infer<typeof CreateListingInput>;

export const PatchListingInput = z.object({
  status: z.enum(LISTING_STATUS).optional(),
  city: z.string().min(2).max(120).optional(),
  zone: z.string().max(120).optional(),
  province: z.string().max(4).optional(),
  region: z.string().max(60).optional(),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  // Portal classification (listing-row columns).
  contract: z.enum(CONTRACT_TYPES).optional(),
  property_type: z.enum(PROPERTY_TYPES).nullable().optional(),
  comune_istat: z.string().max(10).nullable().optional(),
  portals_excluded: z.array(z.enum(PORTAL_KEYS as [string, ...string[]])).max(PORTAL_KEYS.length).optional(),
  // Field-level updates land in content_versions.payload (see fields below).
  fields: z.record(z.enum(ALLOWED_FIELD_KEYS), z.unknown()).optional(),
});
export type PatchListingInput = z.infer<typeof PatchListingInput>;

export const ListingMediaPatchInput = z.object({
  position: z.number().int().min(0).max(100000).optional(),
  caption: z.record(z.string()).optional(),
  alt: z.record(z.string()).optional(),
  is_cover: z.boolean().optional(),
});
export type ListingMediaPatchInput = z.infer<typeof ListingMediaPatchInput>;

export const InquiryPatchInput = z.object({
  status: z.enum(["nuovo", "visto", "in_carico", "chiuso"]).optional(),
  assigned_to: z.string().uuid().nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
});
export type InquiryPatchInput = z.infer<typeof InquiryPatchInput>;

export const ApprovalInput = z.object({
  version_id: z.string().uuid().optional(),
  claim_key: z.enum(ALLOWED_FIELD_KEYS),
  decision: z.enum(["approved", "rejected", "needs_revision"]),
  comment: z.string().max(2000).optional(),
});
export type ApprovalInput = z.infer<typeof ApprovalInput>;
